<template>
  <div class="page-container">
    <div class="page-title-container d-flex align-items-center">
      <router-link :to="{ name: 'medical-leave-list' }" class="button button-light mr-3">
        <i class="ri-arrow-left-line"></i> Volver
      </router-link>
      <h1 class="page-title mb-0">Detalle de descanso médico</h1>
    </div>

    <div v-if="leave">
      <!-- Card: datos del colaborador -->
      <b-card no-body class="custom-card mb-4">
        <b-card-header class="custom-card-header">
          <h3>Datos del colaborador</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div class="row">
            <div class="col-md-4">
              <div class="detail-label text-muted small">Nombre</div>
              <div class="detail-value font-weight-bold">{{ leave.employeeName }}</div>
            </div>
            <div class="col-md-4">
              <div class="detail-label text-muted small">Empresa</div>
              <div class="detail-value">{{ leave.companyName }}</div>
            </div>
            <div class="col-md-4">
              <div class="detail-label text-muted small">Área</div>
              <div class="detail-value">{{ leave.areaName }}</div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Card: datos del descanso -->
      <b-card no-body class="custom-card mb-4">
        <b-card-header class="custom-card-header d-flex justify-content-between align-items-center">
          <h3 class="mb-0">Datos del descanso</h3>
          <span :class="['badge', getStatusColor(leave.status)]">{{ leave.status }}</span>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div class="row">
            <div class="col-md-3">
              <div class="detail-label text-muted small">Tipo</div>
              <div class="detail-value">
                <span :class="['badge', getTypeColor(leave.leaveType)]">{{ getTypeLabel(leave.leaveType) }}</span>
              </div>
            </div>
            <div class="col-md-3">
              <div class="detail-label text-muted small">Fecha inicio</div>
              <div class="detail-value">{{ leave.startDate }}</div>
            </div>
            <div class="col-md-3">
              <div class="detail-label text-muted small">Fecha fin</div>
              <div class="detail-value">{{ leave.endDate }}</div>
            </div>
            <div class="col-md-3">
              <div class="detail-label text-muted small">Días totales</div>
              <div class="detail-value font-weight-bold">{{ leave.calendarDays }}</div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-3">
              <div class="detail-label text-muted small">Días empleador</div>
              <div class="detail-value">{{ leave.employerDays }}</div>
            </div>
            <div class="col-md-3">
              <div class="detail-label text-muted small">Días EsSalud</div>
              <div class="detail-value">{{ leave.essaludDays }}</div>
            </div>
            <div class="col-md-3" v-if="leave.subsidizedDays != null">
              <div class="detail-label text-muted small">Días subsidiados</div>
              <div class="detail-value font-weight-bold text-primary">{{ leave.subsidizedDays }}</div>
            </div>
            <div class="col-md-3" v-if="leave.employeeAge != null">
              <div class="detail-label text-muted small">Edad del empleado</div>
              <div class="detail-value">{{ leave.employeeAge }} años</div>
            </div>
          </div>
          <div class="row mt-3" v-if="leave.cancellationReason">
            <div class="col-12">
              <div class="detail-label text-muted small">Motivo de cancelación</div>
              <div class="detail-value text-danger">{{ leave.cancellationReason }}</div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Card: datos médicos (v-if tipo != PATERNIDAD) -->
      <b-card v-if="leave.leaveType !== 'PATERNIDAD'" no-body class="custom-card mb-4">
        <b-card-header class="custom-card-header">
          <h3>Datos médicos</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div class="row">
            <div class="col-md-3" v-if="leave.cittNumber">
              <div class="detail-label text-muted small">N° CITT</div>
              <div class="detail-value">{{ leave.cittNumber }}</div>
            </div>
            <div class="col-md-3" v-if="leave.medicalInstitution">
              <div class="detail-label text-muted small">Institución médica</div>
              <div class="detail-value">{{ leave.medicalInstitution }}</div>
            </div>
            <div class="col-md-3" v-if="leave.doctorName">
              <div class="detail-label text-muted small">Doctor</div>
              <div class="detail-value">{{ leave.doctorName }}</div>
            </div>
            <div class="col-md-3" v-if="leave.doctorSpecialty">
              <div class="detail-label text-muted small">Especialidad</div>
              <div class="detail-value">{{ leave.doctorSpecialty }}</div>
            </div>
          </div>
          <!-- Diagnóstico (confidencial) -->
          <div class="row mt-3" v-if="leave.diagnosis">
            <div class="col-12">
              <div class="detail-label text-muted small">
                <i class="ri-lock-line mr-1"></i> Diagnóstico — Información confidencial
              </div>
              <div class="detail-value">{{ leave.diagnosis }}</div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Card: subsidio y trámite (v-if caseStatus = SUBSIDIO) -->
      <b-card v-if="leave.caseStatus === 'SUBSIDIO'" no-body class="custom-card mb-4">
        <b-card-header class="custom-card-header">
          <h3>Subsidio y trámite EsSalud</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div class="row">
            <div class="col-md-3">
              <div class="detail-label text-muted small">Estado del caso</div>
              <div class="detail-value">
                <span :class="['badge', getCaseStatusColor(leave.caseStatus)]">
                  {{ getCaseStatusLabel(leave.caseStatus) }}
                </span>
              </div>
            </div>
            <div class="col-md-3" v-if="leave.insuranceType">
              <div class="detail-label text-muted small">Tipo de seguro</div>
              <div class="detail-value">{{ getInsuranceLabel(leave.insuranceType) }}</div>
            </div>
            <div class="col-md-3" v-if="leave.processStatus">
              <div class="detail-label text-muted small">Estado del trámite</div>
              <div class="detail-value">
                <span :class="['badge', getProcessStatusColor(leave.processStatus)]">
                  {{ getProcessStatusLabel(leave.processStatus) }}
                </span>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-3" v-if="leave.dailyPayment != null">
              <div class="detail-label text-muted small">Pago por día</div>
              <div class="detail-value font-weight-bold">S/ {{ Number(leave.dailyPayment).toFixed(2) }}</div>
            </div>
            <div class="col-md-3" v-if="leave.amountPaid != null">
              <div class="detail-label text-muted small">Monto pagado</div>
              <div class="detail-value font-weight-bold text-primary">S/ {{ Number(leave.amountPaid).toFixed(2) }}</div>
            </div>
            <div class="col-md-3" v-if="leave.amountCollected != null">
              <div class="detail-label text-muted small">Monto cobrado</div>
              <div class="detail-value font-weight-bold text-success">S/ {{ Number(leave.amountCollected).toFixed(2) }}</div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Card: gestión -->
      <b-card v-if="leave.personInCharge || leave.attachmentName || leave.observations" no-body class="custom-card mb-4">
        <b-card-header class="custom-card-header">
          <h3>Gestión</h3>
        </b-card-header>
        <b-card-body class="custom-card-body p-4">
          <div class="row">
            <div class="col-md-4" v-if="leave.personInCharge">
              <div class="detail-label text-muted small">Encargado</div>
              <div class="detail-value">{{ leave.personInCharge }}</div>
            </div>
            <div class="col-md-4" v-if="leave.attachmentName">
              <div class="detail-label text-muted small">Documento adjunto</div>
              <div class="detail-value d-flex align-items-center">
                <i class="ri-file-line mr-2 text-primary"></i>
                <span>{{ leave.attachmentName }}</span>
              </div>
            </div>
          </div>
          <div class="row mt-3" v-if="leave.observations">
            <div class="col-12">
              <div class="detail-label text-muted small">Observaciones</div>
              <div class="detail-value">{{ leave.observations }}</div>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Botones de acción -->
      <div v-if="leave.status === 'ACTIVO'" class="d-flex justify-content-end mt-3">
        <button
          type="button"
          class="button button-primary mr-2"
          @click="openFinalize()"
        >
          <i class="ri-check-line mr-1"></i> Finalizar
        </button>
        <button
          type="button"
          class="button button-light"
          @click="openCancel()"
        >
          <i class="ri-forbid-line mr-1"></i> Cancelar
        </button>
      </div>
    </div>

    <!-- Modal: Finalizar -->
    <b-modal
      id="modal-finalize-detail"
      title="Finalizar descanso médico"
      @ok="confirmFinalize"
      ok-title="Finalizar"
      cancel-title="Volver"
      ok-variant="primary"
      :ok-disabled="!finalizeDate"
    >
      <div class="form-group">
        <label>Fecha real de fin <span class="text-danger">*</span></label>
        <FormDatepicker v-model="finalizeDate" />
      </div>
      <div v-if="leave && finalizeDate" class="text-muted small mt-2">
        Días recalculados: {{ computeFinalizeDays() }}
      </div>
    </b-modal>

    <!-- Modal: Cancelar -->
    <b-modal
      id="modal-cancel-detail"
      title="Cancelar descanso médico"
      @ok="confirmCancel"
      ok-title="Cancelar descanso"
      cancel-title="Volver"
      ok-variant="danger"
      :ok-disabled="!cancelReason.trim()"
    >
      <div class="form-group">
        <label>Motivo de cancelación <span class="text-danger">*</span></label>
        <b-form-textarea
          v-model="cancelReason"
          rows="3"
          placeholder="Ingrese el motivo de la cancelación"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert, FormDatepicker } from 'wize-admin'
import { MedicalLeaveService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'MedicalLeaveDetail',
  components: { FormDatepicker },
  data() {
    return {
      leave: null,
      finalizeDate: null,
      cancelReason: '',
    }
  },
  methods: {
    getTypeColor(leaveType) {
      const found = Object.values(Constants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.color : 'badge-gray'
    },
    getTypeLabel(leaveType) {
      const found = Object.values(Constants.MedicalLeaveType).find(t => t.key === leaveType)
      return found ? found.label : leaveType
    },
    getStatusColor(status) {
      const found = Object.values(Constants.MedicalLeaveStatus).find(s => s.key === status)
      return found ? found.color : 'badge-gray'
    },
    getCaseStatusColor(caseStatus) {
      const found = Object.values(Constants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.color : 'badge-gray'
    },
    getCaseStatusLabel(caseStatus) {
      const found = Object.values(Constants.MedicalLeaveCaseStatus).find(s => s.key === caseStatus)
      return found ? found.label : caseStatus
    },
    getProcessStatusColor(processStatus) {
      const found = Object.values(Constants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.color : 'badge-gray'
    },
    getProcessStatusLabel(processStatus) {
      const found = Object.values(Constants.MedicalLeaveProcessStatus).find(s => s.key === processStatus)
      return found ? found.label : processStatus
    },
    getInsuranceLabel(insuranceType) {
      const found = Object.values(Constants.MedicalLeaveInsuranceType).find(s => s.key === insuranceType)
      return found ? found.label : insuranceType
    },
    openFinalize() {
      this.finalizeDate = null
      this.$bvModal.show('modal-finalize-detail')
    },
    openCancel() {
      this.cancelReason = ''
      this.$bvModal.show('modal-cancel-detail')
    },
    computeFinalizeDays() {
      if (!this.leave || !this.finalizeDate) return '-'
      const start = new Date(this.leave.startDate + 'T00:00:00')
      const end   = new Date(this.finalizeDate    + 'T00:00:00')
      return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
    },
    async confirmFinalize() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.finalize(this.leave.id, { actualEndDate: this.finalizeDate })
        Alert.success('Descanso médico finalizado correctamente')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al finalizar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async confirmCancel() {
      try {
        this.$store.dispatch('app/loading', true)
        await MedicalLeaveService.cancel(this.leave.id, { cancellationReason: this.cancelReason })
        Alert.success('Descanso médico cancelado')
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cancelar')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await MedicalLeaveService.get(this.$route.params.id)
        this.leave = resp.payload
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el descanso médico')
        this.$router.push({ name: 'medical-leave-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  created() {
    this.loadData()
  },
}
</script>
